import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../axios/axiosInstance';
import { getActiveCompanyId } from 'utils/companyContext';
import { getEmployees } from 'services/employees';
import { getContractors } from 'services/contractors';
import { getWorkplaces } from 'services/workplaces';
import { createNotification } from 'services/timeOff';

const companyId = () => getActiveCompanyId();

const getTimecards = async ({ employeeId, status }) => {
    const params = {};
    if (employeeId) params.employeeId = employeeId;
    if (status) params.status = status;
    const res = await axiosInstance.get(`/companies/${companyId()}/timecards`, { params });
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
};

const approveTimecard = async (timecardId) => {
    return (await axiosInstance.patch(`/companies/${companyId()}/timecards/${timecardId}/approve`)).data;
};

const fmtDateTime = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
        month: 'short', day: '2-digit', year: '2-digit'
    });
};

const StatusBadge = ({ status }) => {
    const colors = {
        approved: { bg: '#d4edda', color: '#155724' },
        completed: { bg: '#fff3cd', color: '#856404' },
        open:      { bg: '#cce5ff', color: '#004085' },
    };
    const c = colors[status] || colors.open;
    return (
        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: c.bg, color: c.color }}>
            {status}
        </span>
    );
};

const TimeKeeping = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [nameFilter, setNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const { data: timecards = [], isLoading } = useQuery({
        queryKey: ['timecards', statusFilter],
        queryFn: () => getTimecards({ status: statusFilter }),
    });

    const { data: allEmployees = [] } = useQuery({ queryKey: ['employees'], queryFn: getEmployees });
    const { data: allContractorsRaw = [] } = useQuery({ queryKey: ['contractors'], queryFn: getContractors });
    const { data: workplacesRaw = [] } = useQuery({ queryKey: ['workplaces'], queryFn: getWorkplaces });

    const allContractors = Array.isArray(allContractorsRaw) ? allContractorsRaw : [];
    const allWorkplaces  = Array.isArray(workplacesRaw) ? workplacesRaw : (workplacesRaw?.data ?? []);

    // Build lookup maps: id → display name
    const employeeMap = useMemo(() => {
        const m = {};
        allEmployees.forEach(e => {
            const name = [e.firstName, e.lastName].filter(Boolean).join(' ') || e.email || e.id;
            m[e.id] = name;
        });
        return m;
    }, [allEmployees]);

    const contractorMap = useMemo(() => {
        const m = {};
        allContractors.forEach(c => {
            const id = c.id || c.contractorId;
            const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.businessName || c.email || id;
            m[id] = name;
        });
        return m;
    }, [allContractors]);

    const workplaceMap = useMemo(() => {
        const m = {};
        allWorkplaces.forEach(w => { m[w.id || w.uuid] = w.name; });
        return m;
    }, [allWorkplaces]);

    const getPersonName = (t) => {
        if (t.employeeId)  return employeeMap[t.employeeId]  || t.employeeId;
        if (t.contractorId) return contractorMap[t.contractorId] || t.contractorId;
        return '—';
    };

    const getPersonType = (t) => {
        if (t.contractorId && !t.employeeId) return 'Contractor';
        return 'Employee';
    };

    // Client-side name filter
    const filteredTimecards = useMemo(() => {
        if (!nameFilter.trim()) return timecards;
        const q = nameFilter.toLowerCase();
        return timecards.filter(t => {
            const name = getPersonName(t).toLowerCase();
            return name.includes(q);
        });
    }, [timecards, nameFilter, employeeMap, contractorMap]);

    const { mutate: doApprove } = useMutation({
        mutationFn: ({ id }) => approveTimecard(id),
        onSuccess: (_, { timecard }) => {
            queryClient.invalidateQueries(['timecards']);
            const isContractor = !!(timecard.contractorId && !timecard.employeeId);
            const recipientId  = timecard.employeeId || timecard.contractorId;
            const recipientType = isContractor ? 'contractor' : 'employee';
            if (recipientId) {
                const period = [fmtDateTime(timecard.clockInTime), fmtDateTime(timecard.clockOutTime)].filter(Boolean).join(' – ');
                const hours  = timecard.paidHours != null ? ` · ${timecard.paidHours} hrs paid` : '';
                createNotification({
                    recipientId,
                    recipientType,
                    type: 'timecard_approved',
                    title: 'Timecard Approved',
                    message: `Your timecard (${period}${hours}) has been approved.`,
                    companyId: companyId(),
                }).catch(() => {});
            }
        },
    });

    return (
        <div style={{ padding: 24 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                <h4 style={{ fontWeight: 700, margin: 0 }}>Time &amp; Attendance</h4>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button
                        onClick={() => navigate('/dashboard/payroll-time-keeping/add-time-card')}
                        style={{ background: '#1a9fbb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
                    >
                        + Add Timecard
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 20, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <h6 style={{ fontWeight: 700, marginBottom: 16 }}>Timecard Options</h6>
                <div className="row g-3">
                    <div className="col-md-4">
                        <label style={{ fontSize: 13, color: '#6c757d', marginBottom: 4, display: 'block' }}>Search by Name</label>
                        <input
                            className="form-control"
                            placeholder="Employee or contractor name..."
                            value={nameFilter}
                            onChange={e => setNameFilter(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <label style={{ fontSize: 13, color: '#6c757d', marginBottom: 4, display: 'block' }}>Status</label>
                        <select
                            className="form-select"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="open">Open</option>
                            <option value="completed">Completed</option>
                            <option value="approved">Approved</option>
                        </select>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <button
                            onClick={() => { setNameFilter(''); setStatusFilter(''); }}
                            style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontSize: 13, width: '100%' }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                            {['Status', 'Employee / Contractor', 'Type', 'Workplace', 'Clock In', 'Clock Out', 'Total Hours', 'Paid Hours', 'Action'].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #dee2e6' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading timecards...</td></tr>
                        ) : filteredTimecards.length === 0 ? (
                            <tr>
                                <td colSpan={9} style={{ padding: 40, textAlign: 'center' }}>
                                    <div style={{ fontSize: 32, marginBottom: 8 }}>🕐</div>
                                    <div style={{ fontWeight: 600, color: '#555' }}>No timecards found</div>
                                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Timecards appear here when employees or contractors clock in.</div>
                                </td>
                            </tr>
                        ) : filteredTimecards.filter(t => t.employeeId || t.contractorId).map(t => {
                            const isContractor = !!(t.contractorId && !t.employeeId);
                            const wpName = t.workplaceId ? (workplaceMap[t.workplaceId] || t.workplaceId) : '—';
                            return (
                            <tr key={t.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '12px 16px' }}><StatusBadge status={t.status} /></td>
                                <td style={{ padding: '12px 16px', fontWeight: 500 }}>{getPersonName(t)}</td>
                                <td style={{ padding: '12px 16px' }}>
                                    <span style={{
                                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                                        background: isContractor ? '#e8f4fd' : '#e8f5e9',
                                        color: isContractor ? '#0277bd' : '#2e7d32'
                                    }}>
                                        {getPersonType(t)}
                                    </span>
                                </td>
                                <td style={{ padding: '12px 16px' }}>{wpName}</td>
                                <td style={{ padding: '12px 16px' }}>{fmtDateTime(t.clockInTime)}</td>
                                <td style={{ padding: '12px 16px' }}>{fmtDateTime(t.clockOutTime)}</td>
                                <td style={{ padding: '12px 16px' }}>{t.totalHours != null ? `${t.totalHours} hrs` : '—'}</td>
                                <td style={{ padding: '12px 16px' }}>{t.paidHours != null ? `${t.paidHours} hrs` : '—'}</td>
                                <td style={{ padding: '12px 16px' }}>
                                    {t.status === 'completed' && (
                                        <button
                                            onClick={() => doApprove({ id: t.id, timecard: t })}
                                            style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}
                                        >
                                            Approve
                                        </button>
                                    )}
                                    {t.status === 'approved' && (
                                        <span style={{ color: '#28a745', fontWeight: 600, fontSize: 12 }}>✓ Approved</span>
                                    )}
                                    {t.status === 'open' && (
                                        <span style={{ color: '#0066cc', fontSize: 12 }}>In Progress</span>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div style={{ padding: '10px 16px', fontSize: 13, color: '#6c757d', textAlign: 'right' }}>
                    {filteredTimecards.length} timecard{filteredTimecards.length !== 1 ? 's' : ''}
                    {nameFilter && timecards.length !== filteredTimecards.length && ` (filtered from ${timecards.length})`}
                </div>
            </div>

        </div>
    );
};

export default TimeKeeping;